// Minimal avatar with initials fallback. No image source today — every staff
// row is text-only — but the API leaves room to add `avatar_url` later.
//
// Resolution order:
//   1. userId → look up display_name in staff.users via useUserMap
//      → render initials on a tinted circle
//   2. agentId set → robot icon (lucide Bot)
//   3. neither → "?" placeholder
//
// Sizes follow an 8-px grid where it makes sense: xs = 20, sm = 24, md = 32.

import { Bot, HelpCircle } from "lucide-react";

import { cn } from "@/lib/cn";
import { useUserMap } from "@/hooks/useUserMap";

export type UserAvatarSize = "xs" | "sm" | "md";

interface UserAvatarProps {
  userId: string | null | undefined;
  agentId?: string | null;
  size?: UserAvatarSize;
  className?: string;
}

const sizeClasses: Record<UserAvatarSize, string> = {
  xs: "h-5 w-5 text-[10px]",
  sm: "h-6 w-6 text-xs",
  md: "h-8 w-8 text-sm",
};

const iconSizes: Record<UserAvatarSize, string> = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function UserAvatar({
  userId,
  agentId,
  size = "sm",
  className,
}: UserAvatarProps) {
  const { userMap } = useUserMap();
  const sizeCls = sizeClasses[size];
  const iconCls = iconSizes[size];

  // 1) Real user — initials chip.
  if (userId) {
    const user = userMap[userId];
    const name = user?.display_name ?? user?.short_name ?? user?.email ?? null;
    const initials = name ? getInitials(name) : "?";
    return (
      <span
        aria-label={name ?? "Unknown user"}
        title={name ?? undefined}
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground select-none",
          sizeCls,
          className,
        )}
      >
        {initials}
      </span>
    );
  }

  // 2) Agent write — robot glyph.
  if (agentId) {
    return (
      <span
        aria-label={`Agent: ${agentId}`}
        title={`Agent: ${agentId}`}
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary",
          sizeCls,
          className,
        )}
      >
        <Bot className={iconCls} aria-hidden="true" />
      </span>
    );
  }

  // 3) Neither known — placeholder.
  return (
    <span
      aria-label="Unknown editor"
      title="Unknown editor"
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground",
        sizeCls,
        className,
      )}
    >
      <HelpCircle className={iconCls} aria-hidden="true" />
    </span>
  );
}
